import Button from "@/components/forms/Button/Button";
import TextInput from "@/components/forms/TextInput/TextInput";
import Select from "@/components/forms/Select/Select";
import { useEffect, useState } from "react";
import styles from "./page.module.css";
import { AllFormulaOptions, MockFormula, SkuFormula, SkuFormulaItem, SkuFormulaType, formulaTypeToString, makePreviewFormulaName } from "./variable_sku";
import IconDelete from "@/components/icons/iconDelete";
import { SettingsBody, SkuGenerationMode } from "@boxem/core-api-schema";
import { useUserContext } from "@/contexts/UserContext";
import { updateUserSettings } from "@/api/settings";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import SellerAccountBanner from "@/components/SellerAccountBanner/SellerAccountBanner";
import Flex from "@/components/Flex/Flex";
import Alert from "@/components/Alert/Alert";
import LoadingSpinner from "@/components/LoadingSpinner/LoadingSpinner";
import PageContainer from "../container/PageContainer";

//Need to get generation mode and formula from settings instead of from local storage.

export default function VariableSKUPage() {
  const { seller, setSeller } = useUserContext();
  const [skuGenerationOption, setSkuGenerationOption] = useState<SkuGenerationMode>((seller?.settings?.skuGenerationOption as SkuGenerationMode) ?? SkuGenerationMode.boxemGenerated);
  const [formulaOptions, setFormulaOptions] = useState<SkuFormula>(!!seller?.settings?.skuGenerationFormula ? JSON.parse(seller?.settings?.skuGenerationFormula) : MockFormula);
  const [formulaSaving, setFormulaSaving] = useState<boolean>(false);
  const saveButtonDisabled = formulaOptions.findIndex(opt => opt.type === "count") === -1;
  const noChanges = seller?.settings?.skuGenerationFormula === JSON.stringify(formulaOptions);
  const errors = formulaOptions.findIndex(opt => !!opt.error) !== -1;

  const getReadableFormulaPreview = () => {
    let preview = [];

    formulaOptions.map((opt) => {
      preview.push(opt.type);
    });

    return `{${preview.join("}-{")}}`;
  };

  const onSkuGenerationChange = async (e: SkuGenerationMode) => {
    setSkuGenerationOption(e);
    const patch: SettingsBody = {
      id: seller.settings.id,
      skuGenerationOption: e,
    };
    await updateUserSettings(patch).then((settings) => {
      seller.settings.skuGenerationOption = settings.skuGenerationOption;
      setSeller({...seller});
    });
  };

  const onSaveFormula = async () => {
    setFormulaSaving(true);
    const patch: SettingsBody = {
      id: seller.settings.id,
      skuGenerationFormula: JSON.stringify(formulaOptions),
    };
    await updateUserSettings(patch).then((settings) => {
      seller.settings.skuGenerationFormula = settings.skuGenerationFormula;
      setSeller({...seller});
    }).finally(() => setFormulaSaving(false));
  };

  const onResetFormula = () => {
    setFormulaOptions(!!seller?.settings?.skuGenerationFormula ? JSON.parse(seller?.settings?.skuGenerationFormula) : MockFormula);
  };

  const onAddFormulaRule = () => {
    const notUsedOption = AllFormulaOptions.filter((item) => {
      return item.type === SkuFormulaType.custom || !formulaOptions.find((f) => f.type === item.type);
    })[0];

    let newFormulaOptions = [...formulaOptions, { type: notUsedOption.type, label: notUsedOption.label }];
    setFormulaOptions(newFormulaOptions);
  };

  const onChangeFormulaRuleValue = (option: SkuFormulaItem, index: number) => {
    formulaOptions[index].type = option.type;
    setFormulaOptions([...formulaOptions]);
  };

  const onDeleteFormulaRule = (index: number) => {
    const newFormulaOptions = formulaOptions.filter((_, i) => i !== index);
    setFormulaOptions([...newFormulaOptions]);
  };

  const onCustomSkuValueChange = (e: string, index: number) => {
    if (e.match(/^[A-Za-z0-9-]*$/)) {
      formulaOptions[index].error = false;
    } else {
      formulaOptions[index].error = true;
    }
    formulaOptions[index].string = e;
    setFormulaOptions([...formulaOptions]);
  };

  useEffect(() => {
    if (!!seller?.settings) {
      setSkuGenerationOption(seller?.settings?.skuGenerationOption as SkuGenerationMode);
      setFormulaOptions(!!seller?.settings?.skuGenerationFormula ? JSON.parse(seller?.settings?.skuGenerationFormula) : MockFormula);
    }
  }, [seller]);

  return (
    <PageContainer>
      <div className={styles.variableSkuSettingsContainer}>
        <SellerAccountBanner />

        <div className={styles.variableSkuHeader}>SKU Options</div>

        <div className={styles.settingContainer}>
          <div className={styles.settingLabelContainer}>
            <div className={styles.settingLabelHeaderText}>
              SKU Generation
            </div>
            <div className={styles.settingLabelSubText}>
              {skuGenerationOption === SkuGenerationMode.variable && "New SKUs will be generated based on the custom formula you provide below."}
              {skuGenerationOption === SkuGenerationMode.boxemGenerated && "New SKUs will be generated by Boxem when adding a product to a shipment."}
              {skuGenerationOption === SkuGenerationMode.none && "New SKUs are manually entered by you when adding a product to a shipment."}
            </div>
          </div>
          <div className={styles.settingControlsContainer}>

            <div className={styles.settingControlsLabel}>
              Current Generation Method
            </div>
            <Select
              value={skuGenerationOption}
              fullWidth
              onChange={onSkuGenerationChange}
              options={[SkuGenerationMode.none,SkuGenerationMode.boxemGenerated,SkuGenerationMode.variable]}
              selectionRender={option => {
                switch (option) {
                  case SkuGenerationMode.none:
                    return "None";

                  case SkuGenerationMode.boxemGenerated:
                    return "Boxem Generated SKU";

                  case SkuGenerationMode.variable:
                    return "Custom Variable SKU";
                } 
              }}
              optionRender={option => {
                switch (option) {
                  case SkuGenerationMode.none:
                    return (
                      <Flex direction="column">
                        <span>No SKU generation</span>
                        <span className="weak">New SKUs are manually entered by you</span>
                      </Flex>
                    );

                  case SkuGenerationMode.boxemGenerated:
                    return (
                      <Flex direction="column">
                        <span>Boxem Generated SKU</span>
                        <span className="weak">We will randomly generate a New SKU for you</span>
                      </Flex>
                    );

                  case SkuGenerationMode.variable:
                    return (
                      <Flex direction="column">
                        <span>Custom Variable SKU</span>
                        <span className="weak">New SKUs will be generated based on a custom formula you create</span>
                      </Flex>
                    );
                }
              }}
            />
          </div>
        </div>

        {skuGenerationOption === SkuGenerationMode.variable && (
          <>
            <div className={styles.divider} />
            <div className={styles.settingContainer}>
              <div className={styles.settingLabelContainer}>
                <div className={styles.settingLabelHeaderText}>
                  Variable SKU Formula
                </div>
                <div className={styles.settingLabelSubText}>
                  <div className={styles.formulaPreviewDescriptionContainer}>
                    Use up to 5 different variables to construct a custom SKU formula. Count, must always be included in the SKU.
                  </div>
                </div>
              </div>
              <div className={styles.settingControlsContainer}>
                <div className={styles.settingControlsLabel}>
                  Current SKU Formula
                </div>
                <Alert
                  type="warning"
                  message="SKUs have a maximum length of 40 characters. You won't be able to add a product to a shipment if its SKU exceeds 40 characters."
                />
                <div className={styles.space} />
                <div className={styles.formulaPreviewContainer}>
                  <div className={styles.formulaExampleContainer}>
                    <div className={styles.formulaExampleTextContainer}>
                      <div className={styles.formulaPreviewText}>
                        {getReadableFormulaPreview()}
                      </div>
                      <div className={styles.formulaExampleText}>
                        Preview: {makePreviewFormulaName(formulaOptions)}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.space} />
                <div className={styles.formulaControlsContainer}>
                  <div className={styles.formulaControlsLabel}>
                    Formula Variables
                  </div>
                  <Button
                    type="primary"
                    onClick={onAddFormulaRule}
                    disabled={formulaOptions.length === 5}
                  >
                    {formulaOptions.length === 5 ? (
                      <Tooltip title={"You can only use 5 variables"}>
                        Add variable
                      </Tooltip>
                    ) : (
                      "Add variable"
                    )}
                  </Button>
                </div>
                <div className={styles.space} />
                <div className={styles.formulaVariablesContainer}>
                  {formulaOptions.map((option, index) => {
                    const last = index === formulaOptions.length - 1;
                    const current = index + 1;

                    return (
                      <div key={index} className={`${styles.variableContainer} ${!last ? styles.variableBorder : ""}`}>
                        <div className={styles.variableNumberContainer}>
                          {current} of 5
                        </div>
                        <div className={styles.selectDropdownContainer}>
                          <Select
                            fullWidth
                            value={option}
                            options={AllFormulaOptions.filter((item) => {
                              return item.type === SkuFormulaType.custom || !formulaOptions.find((f) => f.type === item.type);
                            })}
                            onChange={option => onChangeFormulaRuleValue(option, index)}
                            optionRender={option => <span className="weak">{formulaTypeToString(option.type)}</span>}
                          />
                          {option.type === SkuFormulaType.custom && (
                            <>
                              <TextInput
                                placeholder="type a short, custom string"
                                type="text"
                                maxLength={30}
                                value={option.string ?? ""}
                                onChange={(e) => onCustomSkuValueChange(e, index)}
                              />
                              {!!option.error && (
                                <div className={`${styles.customInputError} small-text`}>You can only use letters, numbers, and hyphens.</div>
                              )}
                            </>
                          )}
                        </div>
                        <Button
                          type="text"
                          disabled={formulaOptions.length === 1}
                          onClick={() => onDeleteFormulaRule(index)}
                        >
                          <IconDelete size={25} />
                        </Button>
                      </div>
                    );
                  })}
                </div>
                <div className={styles.space} />
                <div className={styles.variableButtonsContainer}>
                  {formulaSaving ? (
                    <LoadingSpinner size="2rem" />
                  ) : (
                    <>
                      <Button
                        type="secondary"
                        onClick={onResetFormula}
                      >
                        Reset
                      </Button>
                      <Button
                        type="primary"
                        onClick={onSaveFormula}
                        disabled={saveButtonDisabled || noChanges || errors}
                      >
                        {(saveButtonDisabled && !noChanges && !errors) && (
                          <Tooltip title={"COUNT must be included in your SKU Formula"}>
                            Save
                          </Tooltip>
                        )}
                        {(!saveButtonDisabled && noChanges && !errors) && (
                          <Tooltip title={"There are no new changes to save"}>
                            Save
                          </Tooltip>
                        )}
                        {(!saveButtonDisabled && !noChanges && errors) && (
                          <Tooltip title={"One or more inputs have errors"}>
                            Save
                          </Tooltip>
                        )}
                        {(!saveButtonDisabled && !noChanges && !errors) && (
                          "Save"
                        )}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </PageContainer>
  );
};